import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { QuoteViewMode, RequestTabId } from '../types'
import {
  buildOrderViewPath,
  encodeOrderView,
  parseOrderView,
} from '../utils/orderViewParams'

const NESTED_TABS: RequestTabId[] = ['checkout', 'delivery']

const isNestedTab = (tab: RequestTabId | undefined) =>
  tab != null && NESTED_TABS.includes(tab)

const LEGACY_REDIRECTS: Record<string, { tab: RequestTabId; mode?: QuoteViewMode }> = {
  sizes: { tab: 'explore' },
  canvas: { tab: 'explore' },
  quote: { tab: 'explore' },
  'quote:canvas': { tab: 'explore' },
  'quote:document': { tab: 'checkout' },
  billing: { tab: 'checkout' },
  'billing:invoice': { tab: 'delivery' },
  proofs: { tab: 'checkout' },
  summary: { tab: 'delivery' },
  shipping: { tab: 'delivery' },
}

export const useOrderViewState = (
  basePath: string,
  options?: { stackNestedViews?: boolean }
) => {
  const { public_id, view } = useParams<{
    public_id: string
    view?: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()
  const parsed = parseOrderView(view)
  const fromGuidedStart = Boolean(
    (location.state as { fromGuidedStart?: boolean } | null)?.fromGuidedStart
  )

  const setView = (
    tab: RequestTabId,
    quoteViewMode: QuoteViewMode = 'canvas'
  ) => {
    if (!public_id) return
    const path = buildOrderViewPath(basePath, public_id, tab, quoteViewMode)
    if (!options?.stackNestedViews) {
      navigate(path, { replace: true })
      return
    }

    const currentIsNested = isNestedTab(parsed?.tab)
    const nextIsNested = isNestedTab(tab)
    const existingState =
      location.state && typeof location.state === 'object'
        ? (location.state as Record<string, unknown>)
        : {}

    if (nextIsNested && !currentIsNested) {
      navigate(path, { state: { ...existingState, fromGuidedStart: true } })
      return
    }

    if (currentIsNested && !nextIsNested && fromGuidedStart) {
      navigate(-1)
      return
    }

    navigate(path, {
      replace: true,
      state: {
        ...existingState,
        fromGuidedStart: nextIsNested ? fromGuidedStart : undefined,
      },
    })
  }

  useEffect(() => {
    if (!public_id || !view) return
    const redirect = LEGACY_REDIRECTS[view]
    if (redirect) {
      const next = encodeOrderView(redirect.tab, redirect.mode)
      if (view === next) return
      navigate(buildOrderViewPath(basePath, public_id, redirect.tab, redirect.mode), {
        replace: true,
      })
      return
    }
    if (view.startsWith('summary:')) {
      navigate(buildOrderViewPath(basePath, public_id, 'delivery'), {
        replace: true,
      })
    }
  }, [basePath, navigate, public_id, view])

  const setTab = (tab: RequestTabId) => {
    setView(tab, parsed?.quoteViewMode ?? 'canvas')
  }

  const setQuoteViewMode = (quoteViewMode: QuoteViewMode) => {
    setView('explore', quoteViewMode)
  }

  return {
    activeTab: parsed?.tab,
    quoteViewMode: parsed?.quoteViewMode ?? 'canvas',
    setTab,
    setQuoteViewMode,
    setView,
    hasViewInUrl: parsed != null,
  }
}
